import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider } from 'react-hook-form';
import { ImagePickerAsset } from 'expo-image-picker';

import { getName } from '@pletly/shared';

import { FormInput } from '../Input';
import { UsernameFormInput } from '../UsernameField';
import { FirstNameField } from '../FirstNameField';
import { LastNameField } from '../LastNameField';
import { ProfilePhotoPicker } from '../ProfilePhotoPicker';
import { EditableItems } from '../EditableItems';
import { CriticalIcon, NegativeIcon, PositiveIcon } from '../../assets';
import { useEditInfoFields, useEditProfilePhotoField, useInfoItems } from '../../hooks';

import { UserFields, EditProfileFormProps } from './types';
import { withTranslation } from '../../locales/hoc';
import { FormToggle } from '../Toggle';
import { CropProfilePhotoModal } from './CropProfilePhotoModal';
import { TagsEditor } from './TagsEditor';

const EditProfileFormComponent = <User extends UserFields>({
	user,
	isCareReceiver = false,
	canFullyEditCareRecipient = false,
	renderActions,
	onSubmit,
	children,
	tagsInfo = { canEdit: false, availableNames: [] },
}: EditProfileFormProps<User>) => {
	const { t } = useTranslation();

	const { formMethods, isChanged } = useEditInfoFields(
		{
			email: isCareReceiver ? undefined : (user.email ?? ''),
			username: user.username,
			info: user.info ?? '',
			firstName: user.firstName ?? '',
			lastName: user.lastName ?? '',
			restrictedAccess: user.restrictedAccess ?? undefined,
			receiveSummaryNotificationEmails: user.receiveSummaryNotificationEmails ?? undefined,
			tags: user.tags ?? undefined,
		},
		{
			isCareReceiverProfile: isCareReceiver,
			canFullyEditCareRecipient,
			canEditTags: tagsInfo.canEdit,
		},
	);
	const photo = useEditProfilePhotoField(user.photoSource);
	const { criticalInfo = [], positiveInfo = [], negativeInfo = [] } = user.profile || {};
	const {
		criticalItems,
		positiveItems,
		negativeItems,
		setCriticalItems,
		setNegativeItems,
		setPositiveItems,
		itemsChanged,
	} = useInfoItems({ criticalInfo, positiveInfo, negativeInfo });

	const canSubmit = isChanged || itemsChanged || photo.isChanged;

	const updateUser: Parameters<typeof formMethods.handleSubmit>['0'] = useCallback(
		({
			email,
			username,
			firstName,
			lastName,
			info,
			restrictedAccess,
			receiveSummaryNotificationEmails,
			tags,
		}) => {
			if (!canSubmit) return;

			return onSubmit({
				inputs: {
					email,
					username,
					firstName,
					lastName,
					info,
					restrictedAccess,
					receiveSummaryNotificationEmails,
					tags,
				},
				newPhotoAsset: photo.isChanged ? photo.asset : undefined,
				additionalInfoItems: isCareReceiver
					? {
							critical: criticalItems,
							negative: negativeItems,
							positive: positiveItems,
						}
					: undefined,
			});
		},
		[
			canSubmit,
			photo.isChanged,
			photo.asset,
			isCareReceiver,
			criticalItems,
			negativeItems,
			positiveItems,
			onSubmit,
		],
	);

	const [imageToCrop, setImageToCrop] = useState<ImagePickerAsset | null>(null);

	const showCropModal = setImageToCrop;

	const hideCropModal = useCallback(() => {
		setImageToCrop(null);
	}, []);

	const handleSaveCroppedPhoto = useCallback(
		(croppedImage: ImagePickerAsset) => {
			photo.setAsset({ ...croppedImage, type: croppedImage.type || 'image' });
			hideCropModal();
		},
		[hideCropModal, photo],
	);

	return (
		<FormProvider {...formMethods} key={user.id}>
			<ProfilePhotoPicker
				username={getName(user)}
				photoSource={photo.source}
				onImagePicked={showCropModal}
			/>

			<UsernameFormInput fullWidth showGenerator accessoryLeft={undefined} />

			<FormInput
				InputComponent={FirstNameField}
				fullWidth
				optional={isCareReceiver}
				name="firstName"
			/>

			<FormInput
				InputComponent={LastNameField}
				fullWidth
				optional={isCareReceiver}
				name="lastName"
			/>

			{!isCareReceiver && (
				<FormInput
					testID="email"
					fullWidth
					textContentType="emailAddress"
					label={t('edit-profile-form.email')}
					placeholder={t('edit-profile-form.email')}
					name="email"
				/>
			)}

			<FormInput
				gutter
				textContentType="none"
				label={t('edit-profile-form.about-me')}
				placeholder={t('edit-profile-form.about-me')}
				multiline
				fullWidth
				numberOfLines={3}
				name="info"
			/>

			{isCareReceiver && (
				<>
					<View>
						<EditableItems
							Icon={CriticalIcon}
							title={t('edit-profile-form.critical')}
							helpText={t('edit-profile-form.tooltip.critical')}
							items={criticalItems}
							onChange={setCriticalItems}
						/>
						<EditableItems
							Icon={PositiveIcon}
							title={t('edit-profile-form.positive')}
							helpText={t('edit-profile-form.tooltip.positive')}
							items={positiveItems}
							onChange={setPositiveItems}
						/>
						<EditableItems
							Icon={NegativeIcon}
							title={t('edit-profile-form.negative')}
							helpText={t('edit-profile-form.tooltip.negative')}
							items={negativeItems}
							onChange={setNegativeItems}
						/>
					</View>

					{tagsInfo.canEdit && (
						<Controller
							name="tags"
							control={formMethods.control}
							render={({ field: { onChange, value } }) => (
								<TagsEditor
									suggestions={tagsInfo.availableNames}
									onChange={onChange}
									value={value}
								/>
							)}
						/>
					)}

					{canFullyEditCareRecipient && (
						<FormToggle name="restrictedAccess" label={t('edit-profile-form.restricted-access')} />
					)}
				</>
			)}

			{children}

			{renderActions({
				canSubmit,
				submit: formMethods.handleSubmit(updateUser),
			})}
			<CropProfilePhotoModal
				imageToCrop={imageToCrop}
				onClose={hideCropModal}
				onSave={handleSaveCroppedPhoto}
			/>
		</FormProvider>
	);
};

export const EditProfileForm = withTranslation(EditProfileFormComponent);
